import React, { useRef, useState, useEffect } from 'react'
import { CSSTransition } from 'react-transition-group'
import classNames from 'classnames'
import { Props } from './types'
import useStore from '@/store'
import CloseButton from '@/components/close-button'
import useMeasure from '@/hooks/use-measure'
import useOutsideClick from '@/hooks/use-outside-click'
import styles from './style.module.scss'

const Modal: React.FC<Props> = ({ content, side, type, closeAction }) => {
  const animElRef = useRef<HTMLDivElement | null>(null)
  const innerElRef = useRef<HTMLDivElement | null>(null)
  const [renderContent, setRenderContent] = useState<React.ReactNode>(null)
  const setModal = useStore((state) => state.setModal)
  const onPointerMove = useMeasure('modal', true, innerElRef)

  useOutsideClick(
    innerElRef.current,
    () => setModal({ content: undefined }, side),
    animElRef.current
  )

  useEffect(() => {
    content && setRenderContent(content)
    // eslint-disable-next-line
  }, [content])

  const classes = classNames(styles['modal'], [
    styles[`modal--side-${side}`],
    styles[`modal--type-${type}`],
  ])

  return (
    <CSSTransition
      appear={true}
      in={!!content}
      nodeRef={animElRef}
      mountOnEnter={true}
      unmountOnExit={true}
      classNames={{
        appear: styles['modal--appear'],
        appearActive: styles['modal--appear-active'],
        enter: styles['modal--enter'],
        enterActive: styles['modal--enter-active'],
        exit: styles['modal--exit'],
        exitActive: styles['modal--exit-active'],
      }}
      timeout={500}
    >
      <div ref={animElRef} className={classes}>
        <div ref={innerElRef} className={styles.inner}
          onPointerMove={onPointerMove}>
          {renderContent}
          <CloseButton
            clickHandler={() => closeAction && closeAction()}
            type={type}
          />
        </div>
      </div>
    </CSSTransition>
  )
}

export default React.memo(
  Modal,
  (prev, next) => prev.content?.toString() === next.content?.toString()
)
